import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { client, csrfHeader, unwrap } from "@/api/client";
import type { components } from "@/api/schema";
import { t } from "@/i18n";

export type Conversation = components["schemas"]["Conversation"];
export type Message = components["schemas"]["Message"];
export type MessageMedia = components["schemas"]["MessageMedia"];
export type MessageTemplate = components["schemas"]["MessageTemplate"];
export type ConversationStatus = Conversation["status"];
export type MessageStatus = Message["status"];

/** Which slice of the inbox is on screen. */
export type InboxFilter = "mine" | "unassigned" | "all";

export const INBOUND = 1;
export const OUTBOUND = 2;

export function threadKey(conversationId: number) {
  return ["messages", conversationId] as const;
}

export function useConversations(filter: InboxFilter, agentId: number | null) {
  return useQuery({
    queryKey: ["conversations", filter, agentId],
    // "Mías" is not answerable until we know who that is. Firing early would
    // fetch everything this agent may see, paint it, and then narrow — so the
    // inbox would open showing threads that are not theirs.
    enabled: filter !== "mine" || agentId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/whatsapp/conversations/", {
          params: {
            query: {
              ordering: "-last_message_at",
              // The server already limits what this agent may see; these
              // narrow *within* that. "Todas" is not "everyone's" — for a
              // seller it is still only their own plus the unassigned queue.
              ...(filter === "unassigned" ? { status: "unassigned" } : {}),
              ...(filter === "mine" && agentId !== null ? { agent: agentId } : {}),
            },
          },
        }),
        t("errors.inbox"),
      ),
    placeholderData: keepPreviousData,
    // The socket invalidates this on every inbound message, so a poll would be
    // redundant — but a socket that has been down is exactly when the inbox
    // must not be stale, and `onReconnect` already refetches everything.
  });
}

/**
 * The red badge on *WhatsApp*: threads waiting for this agent with messages
 * nobody has opened.
 *
 * Keyed under `conversations`, so every inbound message, assignment and
 * mark-as-read the socket reports refreshes it without a poll.
 */
export function useUnreadConversations() {
  return useQuery({
    queryKey: ["conversations", "unread"],
    queryFn: () =>
      unwrap(client.GET("/api/whatsapp/conversations/unread/"), t("errors.unread")),
    // The one number on screen that must not be stale when someone comes back
    // to a tab that sat in the background.
    refetchOnWindowFocus: true,
  });
}

export function useConversation(id: number | null) {
  return useQuery({
    queryKey: ["conversation", id],
    enabled: id !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/whatsapp/conversations/{id}/", {
          params: { path: { id: id as number } },
        }),
        t("errors.conversation"),
      ),
  });
}

/**
 * The thread, newest page first.
 *
 * The action orders `-timestamp`, so page 1 is what the agent sees on open and
 * each further page is older — "cargar anteriores" walks backwards through a
 * relationship that may be fifteen years long, and never has to count the
 * pages first to find the end.
 */
export function useThread(conversationId: number | null) {
  return useInfiniteQuery({
    queryKey: conversationId === null ? ["messages", null] : threadKey(conversationId),
    enabled: conversationId !== null,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      unwrap(
        client.GET("/api/whatsapp/conversations/{id}/messages/", {
          params: {
            path: { id: conversationId as number },
            query: { page: pageParam },
          },
        }),
        t("errors.messages"),
      ),
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
  });
}

/**
 * Oldest-first, for rendering.
 *
 * The API hands back newest-first because that is the right pagination order;
 * a thread is *read* in the other direction. Flattening and reversing here
 * keeps that inversion in one place instead of in every component that touches
 * a message list.
 */
export function messagesInReadingOrder(
  pages: { results: Message[] }[] | undefined,
): Message[] {
  if (!pages) return [];
  return pages.flatMap((page) => page.results).reverse();
}

/** Text, an approved template, or a file with an optional caption in `body`. */
export type SendInput =
  { body: string } | { template: number } | { file: File; body: string };

/**
 * The formats Meta takes, by extension, and the WhatsApp type each goes out as.
 *
 * A copy of the server's `attachments.FORMATS`, which is the one that decides:
 * this one only narrows the file picker and dresses the optimistic bubble.
 */
export const SENDABLE_FILES = {
  image: [".jpg", ".jpeg", ".png"],
  video: [".mp4", ".3gp"],
  audio: [".aac", ".amr", ".mp3", ".m4a", ".ogg"],
  document: [".pdf", ".txt", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx"],
} as const;

export const SENDABLE_ACCEPT = Object.values(SENDABLE_FILES).flat().join(",");

export function fileKind(name: string): keyof typeof SENDABLE_FILES {
  const extension = name.slice(name.lastIndexOf(".")).toLowerCase();
  const kinds = Object.keys(SENDABLE_FILES) as (keyof typeof SENDABLE_FILES)[];
  return (
    kinds.find((kind) =>
      (SENDABLE_FILES[kind] as readonly string[]).includes(extension),
    ) ?? "document"
  );
}

/** A not-yet-saved message, so the bubble appears on the same tick as the click. */
export function optimisticMessage(input: SendInput, at = Date.now()): Message {
  const file = "file" in input ? input.file : null;
  return {
    // Negative, so it can never collide with a real pk and is obvious in a
    // React key or a debugger.
    id: -at,
    wamid: null,
    direction: OUTBOUND,
    kind: "template" in input ? "template" : file ? fileKind(file.name) : "text",
    status: "queued",
    body: "template" in input ? "" : input.body,
    replies_to: null,
    sent_by: null,
    template: "template" in input ? input.template : null,
    pricing_category: "",
    is_billable: false,
    error_code: "",
    error_message: "",
    timestamp: new Date(at).toISOString(),
    delivered_at: null,
    read_at: null,
    // Not downloadable yet: the bubble names the file until the server's row,
    // with a copy it can serve, replaces this one.
    media: file
      ? [
          {
            id: -at,
            mime_type: file.type,
            filename: file.name,
            size_bytes: file.size,
            is_downloaded: false,
          },
        ]
      : [],
  };
}

/**
 * A file, and its caption.
 *
 * Not through `client`: a multipart body needs the browser's own boundary,
 * which `openapi-fetch` cannot express — so this call adds the CSRF header
 * itself. The answer still goes through `unwrap`, so a refusal ("WhatsApp no
 * acepta este tipo de archivo…") reads the same as any other.
 */
async function sendFile(
  conversationId: number,
  input: { file: File; body: string },
): Promise<Message> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.body) form.append("body", input.body);
  const response = await fetch(
    `/api/whatsapp/conversations/${conversationId}/messages/`,
    {
      method: "POST",
      body: form,
      credentials: "same-origin",
      headers: csrfHeader(),
    },
  );
  const payload: unknown = await response.json().catch(() => undefined);
  return unwrap(
    Promise.resolve(
      response.ok
        ? { data: payload as Message, response }
        : { error: payload ?? {}, response },
    ),
    t("errors.send"),
  );
}

export function isOptimistic(message: Message): boolean {
  return message.id < 0;
}

/**
 * Send, showing the bubble immediately.
 *
 * The row is persisted server-side before the Graph call, so `queued` is a
 * real state and not a fiction — the optimistic bubble is showing the same
 * thing the server is about to show, one round trip early. It then walks
 * `enviado → entregado → leído` from socket events.
 *
 * On failure the bubble is rolled back and the error is returned to the
 * composer, which keeps the text and offers a retry. A reply that vanishes on
 * a bad network is how an agent ends up believing they answered a client they
 * did not.
 */
export function useSendMessage(conversationId: number) {
  const queryClient = useQueryClient();
  const key = threadKey(conversationId);

  return useMutation({
    mutationFn: (input: SendInput) =>
      "file" in input
        ? sendFile(conversationId, input)
        : unwrap(
            client.POST("/api/whatsapp/conversations/{id}/messages/", {
              params: { path: { id: conversationId } },
              body: input,
            }),
            t("errors.send"),
          ),

    onMutate: async (input) => {
      // An in-flight refetch would land after the optimistic insert and undo
      // it, which looks exactly like the message being rejected.
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      const pending = optimisticMessage(input);

      queryClient.setQueryData(
        key,
        (
          data: { pages: { results: Message[] }[]; pageParams: unknown[] } | undefined,
        ) => {
          if (!data) return data;
          const [first, ...rest] = data.pages;
          if (!first) return data;
          // Page 1 is the newest, and within it the newest is first.
          return {
            ...data,
            pages: [{ ...first, results: [pending, ...first.results] }, ...rest],
          };
        },
      );
      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(key, context.previous);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      // `last_message_at` and `status` moved, so the list re-sorts.
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
      void queryClient.invalidateQueries({
        queryKey: ["conversation", conversationId],
      });
    },
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(
        client.POST("/api/whatsapp/conversations/{id}/read/", {
          params: { path: { id } },
        }),
        t("errors.markRead"),
      ),
    onSuccess: (conversation) => {
      queryClient.setQueryData(["conversation", conversation.id], conversation);
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useAssign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      agent,
      reason,
    }: {
      id: number;
      agent: number | null;
      reason?: string;
    }) =>
      unwrap(
        client.POST("/api/whatsapp/conversations/{id}/assign/", {
          params: { path: { id } },
          body: { agent, ...(reason ? { reason } : {}) },
        }),
        t("errors.assign"),
      ),
    onSuccess: (conversation) => {
      queryClient.setQueryData(["conversation", conversation.id], conversation);
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/** Attach an unknown sender to a client record — the Recepción flow (.docs/01 §4.6). */
export function useLinkConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      company,
      contact,
    }: {
      id: number;
      company?: number;
      contact?: number;
    }) =>
      unwrap(
        client.POST("/api/whatsapp/conversations/{id}/link/", {
          params: { path: { id } },
          body: {
            ...(company !== undefined ? { company } : {}),
            ...(contact !== undefined ? { contact } : {}),
          },
        }),
        t("errors.link"),
      ),
    onSuccess: (conversation) => {
      queryClient.setQueryData(["conversation", conversation.id], conversation);
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}

/**
 * *WhatsApp* on a contact card: the thread with that number, opened if need be.
 *
 * The server refuses a contact with no recorded opt-in (.docs/06 §2.1), and its
 * sentence is what the card shows.
 */
export function useStartConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (phone: number) =>
      unwrap(
        client.POST("/api/whatsapp/conversations/start/", { body: { phone } }),
        t("errors.startConversation"),
      ),
    onSuccess: (conversation) => {
      queryClient.setQueryData(["conversation", conversation.id], conversation);
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useCloseConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(
        client.POST("/api/whatsapp/conversations/{id}/close/", {
          params: { path: { id } },
        }),
        t("errors.closeConversation"),
      ),
    onSuccess: (conversation) => {
      queryClient.setQueryData(["conversation", conversation.id], conversation);
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

/** The number a client should write to — what *Pedir WhatsApp* links to. */
export function useSendingNumber() {
  return useQuery({
    queryKey: ["whatsapp-number", "mine"],
    queryFn: () =>
      unwrap(client.GET("/api/whatsapp/numbers/mine/"), t("errors.sendingNumber")),
    // Which number an agent sends from is an admin's decision, not a live one.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/** A template as this client will read it, blanks filled in by the server. */
export function useTemplatePreview(conversationId: number, templateId: number | null) {
  return useQuery({
    queryKey: ["template-preview", conversationId, templateId],
    enabled: templateId !== null,
    queryFn: () =>
      unwrap(
        client.GET("/api/whatsapp/conversations/{id}/template-preview/", {
          params: {
            path: { id: conversationId },
            query: { template: templateId as number },
          },
        }),
        t("errors.templatePreview"),
      ),
  });
}

/** What the composer offers once the window has closed. Approved templates only. */
export function useTemplates() {
  return useQuery({
    queryKey: ["templates"],
    queryFn: () =>
      unwrap(
        // Explicitly approved: management's list holds every status, and the
        // picker must never offer one Meta has not signed off.
        client.GET("/api/whatsapp/templates/", {
          params: { query: { status: "APPROVED" } },
        }),
        t("errors.templates"),
      ),
    // Approval is a Meta review queue measured in days, not seconds.
    staleTime: 5 * 60_000,
  });
}

/**
 * Where an attachment lives. A URL, not a fetch: the endpoint 302s to a
 * short-lived presigned object and the browser follows it. WhatsApp media is
 * client PII — never a public object, and the bucket is never listable.
 */
export function mediaUrl(id: number): string {
  return `/api/whatsapp/media/${id}/`;
}
